import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useConfig } from '../context/ConfigContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Shared by the editable title and subtitle boxes in the results view.
// fontSize stays at 16px: below that, iOS zooms the viewport when the field takes
// focus, which on a phone leaves the admin scrolled sideways mid-edit.
const editableFieldStyle = {
  width: '100%',
  padding: '10px',
  fontSize: '16px',
  borderRadius: '4px',
  border: '1px solid var(--color-border)',
  minHeight: '44px',
  boxSizing: 'border-box',
  marginBottom: '6px'
};

// Over the limit the field and its counter both go red. eBay rejects the value
// outright rather than truncating it, so this is an error state, not a nudge.
const overLimitStyle = (value, max) =>
  value.length > max ? { border: '2px solid var(--color-error-border)' } : {};

const counterStyle = (value, max) => ({
  fontSize: '13px',
  textAlign: 'right',
  marginBottom: '10px',
  fontWeight: value.length > max ? 'bold' : 'normal',
  color: value.length > max ? '#c62828' : '#666'
});

function EbayListingGenerator() {
  const navigate = useNavigate();
  // No financing tiers read here any more. This page used to pull the tenant's
  // tiers only to re-derive the payment table the server had already computed,
  // which is how the two came to disagree. The server returns the rows it
  // publishes; this page renders them.
  const { isDemoMode } = useConfig();
  const demoDisabled = isDemoMode();
  const [inputMode, setInputMode] = useState('existing'); // 'existing' or 'manual'
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedListing, setGeneratedListing] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');

  // The title and subtitle the server generated are a STARTING POINT, not the
  // answer. eBay's own limits are hard (80 and 55), the generator has no way to
  // know which words sell a particular parcel, and the previous version showed
  // both in read-only boxes whose Copy buttons were the only way to get the text
  // out. These hold the admin's edits, seeded from the server on each generate
  // and reset when they go back through "Edit Information".
  const [editedTitle, setEditedTitle] = useState('');
  const [editedSubtitle, setEditedSubtitle] = useState('');

  const EBAY_TITLE_MAX = 80;
  const EBAY_SUBTITLE_MAX = 55;

  // Zoning for the "select an existing property" path. Prefilled from the saved
  // property's own zoning column and editable per listing. Blank is meaningful:
  // it omits the Zoning row from the generated HTML rather than printing a guess.
  const [existingZoning, setExistingZoning] = useState('');

  // Manual entry form data
  const [manualData, setManualData] = useState({
    acres: '',
    location: '',
    city: '',
    state: '',
    county: '',
    price: '',
    apn: '',
    zoning: '',
    annual_tax_amount: '',
    monthly_poa_fee: ''
  });

  // Custom description
  const [customDescription, setCustomDescription] = useState('');

  // THE AUCTION BLOCK. Stored per tenant and applied to every listing, so this
  // box holds the terms of the sale rather than the parcel copy. The textarea is
  // the working copy: editing it changes the next listing generated, and only
  // "Save for all listings" writes it back to the tenant's setting.
  const [auctionBlock, setAuctionBlock] = useState('');
  const [auctionBlockDefault, setAuctionBlockDefault] = useState('');
  const [auctionBlockOpen, setAuctionBlockOpen] = useState(false);
  const [auctionBlockSaving, setAuctionBlockSaving] = useState(false);
  const [auctionBlockStatus, setAuctionBlockStatus] = useState('');

  useEffect(() => {
    fetchProperties();
    fetchAuctionBlock();
  }, []);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/admin/ebay/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data.properties || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const fetchAuctionBlock = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/admin/ebay/auction-block`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuctionBlock(response.data.template || '');
      setAuctionBlockDefault(response.data.defaultTemplate || '');
    } catch (error) {
      console.error('Error fetching auction block:', error);
    }
  };

  const handleSaveAuctionBlock = async () => {
    setAuctionBlockSaving(true);
    setAuctionBlockStatus('');
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API_URL}/admin/ebay/auction-block`,
        { template: auctionBlock },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setAuctionBlockStatus('saved');
      setTimeout(() => setAuctionBlockStatus(''), 3000);
    } catch (error) {
      console.error('Error saving auction block:', error);
      setAuctionBlockStatus('error');
    } finally {
      setAuctionBlockSaving(false);
    }
  };

  const handlePropertySelect = (e) => {
    const propertyId = e.target.value;
    setSelectedPropertyId(propertyId);
    
    // Auto-fill description and zoning from selected property
    if (propertyId) {
      const property = properties.find(p => p.id === parseInt(propertyId));
      if (property) {
        setCustomDescription(property.description || '');
        setExistingZoning(property.zoning || '');
      }
    } else {
      setExistingZoning('');
    }
  };

  const handleManualChange = (e) => {
    const { name, value } = e.target;
    setManualData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenerateListing = async () => {
    setLoading(true);
    setCopySuccess('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        customDescription,
        // Sent every time, so an unsaved tweak in the Auction Block box shows up
        // in this listing. Persisting it is a separate, explicit Save.
        auctionBlock
      };

      if (inputMode === 'existing') {
        if (!selectedPropertyId) {
          alert('Please select a property');
          setLoading(false);
          return;
        }
        payload.propertyId = selectedPropertyId;
        // Always sent, including as an empty string, which is how the server is
        // told to leave the Zoning row out entirely.
        payload.zoning = existingZoning;
      } else {
        // Validate manual data
        if (!manualData.acres || !manualData.location || !manualData.state || 
            !manualData.county || !manualData.price) {
          alert('Please fill in all required fields (Acres, Location, State, County, Price)');
          setLoading(false);
          return;
        }
        payload.manualData = manualData;
      }

      const response = await axios.post(
        `${API_URL}/admin/ebay/generate-listing`,
        payload,
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setGeneratedListing(response.data);
      setEditedTitle(response.data.title || '');
      setEditedSubtitle(response.data.subtitle || '');
    } catch (error) {
      console.error('Error generating listing:', error);
      alert('Failed to generate listing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(type);
      setTimeout(() => setCopySuccess(''), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <button onClick={() => navigate('/admin/tools-hub')} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">←</span>
          Back to Tools
        </button>
        <h1 className="admin-page-title">📦 eBay Listing Generator</h1>
      </div>

      {!generatedListing ? (
        <>
          {/* Input Mode Selection */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>Choose Input Method</h3>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px' }}>
                <input
                  type="radio"
                  value="existing"
                  checked={inputMode === 'existing'}
                  onChange={(e) => setInputMode(e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Select Existing Property
              </label>
              <label style={{ display: 'block' }}>
                <input
                  type="radio"
                  value="manual"
                  checked={inputMode === 'manual'}
                  onChange={(e) => setInputMode(e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Manual Entry
              </label>
            </div>
          </div>

          {/* Property Selection or Manual Entry */}
          {inputMode === 'existing' ? (
            <div className="card" style={{ marginBottom: '20px' }}>
              <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>Select Property</h3>
              <select
                value={selectedPropertyId}
                onChange={handlePropertySelect}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  borderRadius: '4px',
                  border: '1px solid var(--color-border)'
                }}
              >
                <option value="">-- Select a Property --</option>
                {properties.map(prop => (
                  <option key={prop.id} value={prop.id}>
                    {prop.title} - {prop.location} - ${prop.price.toLocaleString()}
                  </option>
                ))}
              </select>

              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Zoning
                </label>
                <p style={{ fontSize: '14px', color: 'var(--color-text-light)', margin: '0 0 8px 0' }}>
                  Prefilled from the property record. Leave blank to leave zoning out
                  of the listing. Only enter what the county confirms.
                </p>
                <input
                  type="text"
                  value={existingZoning}
                  onChange={(e) => setExistingZoning(e.target.value)}
                  placeholder="Leave blank to omit"
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '4px',
                    border: '1px solid var(--color-border)',
                    minHeight: '44px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="card" style={{ marginBottom: '20px' }}>
              <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>Property Information</h3>
              <div style={{ display: 'grid', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Acres *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="acres"
                    value={manualData.acres}
                    onChange={handleManualChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Location (e.g., "Near Denver, Colorado") *
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={manualData.location}
                    onChange={handleManualChange}
                    placeholder="Near Denver, Colorado"
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    City/Area
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={manualData.city}
                    onChange={handleManualChange}
                    placeholder="Denver"
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    State *
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={manualData.state}
                    onChange={handleManualChange}
                    placeholder="Colorado"
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    County *
                  </label>
                  <input
                    type="text"
                    name="county"
                    value={manualData.county}
                    onChange={handleManualChange}
                    placeholder="Jefferson"
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Price *
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={manualData.price}
                    onChange={handleManualChange}
                    placeholder="5000"
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    APN
                  </label>
                  <input
                    type="text"
                    name="apn"
                    value={manualData.apn}
                    onChange={handleManualChange}
                    placeholder="123-45-678-90"
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Zoning
                  </label>
                  <p style={{ fontSize: '14px', color: 'var(--color-text-light)', margin: '0 0 8px 0' }}>
                    Leave blank to leave zoning out of the listing. Only enter what
                    the county confirms.
                  </p>
                  <input
                    type="text"
                    name="zoning"
                    value={manualData.zoning}
                    onChange={handleManualChange}
                    placeholder="Leave blank to omit"
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Annual Tax Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="annual_tax_amount"
                    value={manualData.annual_tax_amount}
                    onChange={handleManualChange}
                    placeholder="120"
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Monthly POA Fee
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="monthly_poa_fee"
                    value={manualData.monthly_poa_fee}
                    onChange={handleManualChange}
                    placeholder="0"
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      minHeight: '44px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Custom Description */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>Custom Description for eBay</h3>
            <p style={{ fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '10px' }}>
              Describe terrain, access, utilities, views, nearby towns, and permitted uses.
            </p>
            <textarea
              value={customDescription}
              onChange={(e) => setCustomDescription(e.target.value)}
              placeholder="This beautiful property offers..."
              rows="8"
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                borderRadius: '4px',
                border: '1px solid var(--color-border)',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Auction Block */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <button
              onClick={() => setAuctionBlockOpen(open => !open)}
              style={{
                width: '100%',
                minHeight: '44px',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit'
              }}
              aria-expanded={auctionBlockOpen}
            >
              <span style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--color-heading)' }}>
                Auction Block (applies to every listing)
              </span>
              <span style={{ fontSize: '18px', color: 'var(--color-heading)', flexShrink: 0 }}>
                {auctionBlockOpen ? '▲' : '▼'}
              </span>
            </button>

            {auctionBlockOpen && (
              <div style={{ marginTop: '10px' }}>
                <p style={{ fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '10px' }}>
                  This text is placed at the top of the Property Description, above the
                  parcel copy, on every listing you generate. Fields written as
                  {' {{PRICE}}'} are replaced with the real numbers when the listing is
                  generated, using the same figures as the financing table. A field that
                  cannot be filled is left out rather than printed. A line in all capitals
                  becomes a heading.
                </p>
                <p style={{ fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '10px' }}>
                  Available fields: {'{{PRICE}}'}, {'{{DOC_FEE}}'}, {'{{TERM_RANGE}}'},{' '}
                  {'{{MIN_DOWN}}'}, {'{{EXAMPLE_TERM}}'}, {'{{MIN_DOWN_MONTHLY}}'},{' '}
                  {'{{TIER_SENTENCES}}'}
                </p>
                <textarea
                  value={auctionBlock}
                  onChange={(e) => {
                    setAuctionBlock(e.target.value);
                    setAuctionBlockStatus('');
                  }}
                  rows="16"
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '4px',
                    border: '1px solid var(--color-border)',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    marginBottom: '10px'
                  }}
                />
                <p style={{ fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '10px' }}>
                  Edits here apply to the next listing you generate. They are saved for
                  future listings only when you press Save.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  <button
                    onClick={() => !demoDisabled && handleSaveAuctionBlock()}
                    disabled={auctionBlockSaving || demoDisabled}
                    className="btn btn-primary"
                    style={{
                      flex: '1 1 200px',
                      minHeight: '44px',
                      backgroundColor: auctionBlockStatus === 'saved' ? '#10b981' : '#2c5f2d',
                      cursor: (auctionBlockSaving || demoDisabled) ? 'not-allowed' : 'pointer'
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    {auctionBlockSaving
                      ? 'Saving...'
                      : auctionBlockStatus === 'saved'
                        ? '✓ Saved for all listings'
                        : 'Save for all listings'}
                  </button>
                  <button
                    onClick={() => {
                      setAuctionBlock(auctionBlockDefault);
                      setAuctionBlockStatus('');
                    }}
                    className="btn btn-secondary"
                    style={{ flex: '1 1 200px', minHeight: '44px' }}
                  >
                    Reset to default
                  </button>
                </div>
                {auctionBlockStatus === 'error' && (
                  <p style={{ fontSize: '14px', color: 'var(--color-error-text)', marginTop: '10px' }}>
                    Could not save the auction block. Please try again.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Generate Button */}
          <button
            onClick={() => !demoDisabled && handleGenerateListing()}
            disabled={loading || demoDisabled}
            className="btn btn-primary btn-full-width"
            style={{
              padding: '15px',
              fontSize: '18px',
              fontWeight: 'bold',
              backgroundColor: (loading || demoDisabled) ? '#ccc' : '#f4a460',
              cursor: (loading || demoDisabled) ? 'not-allowed' : 'pointer'
            }}
            title={demoDisabled ? 'Disabled in demo mode' : ''}
          >
            {loading ? 'Generating...' : '🎨 Generate Listing Preview'}
          </button>
        </>
      ) : (
        <>
          {/* Preview Section */}
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => {
                setGeneratedListing(null);
                setCopySuccess('');
                // Edits are scoped to one generated listing. Going back here is
                // the one action that discards them; the next generate reseeds
                // both fields from the server.
                setEditedTitle('');
                setEditedSubtitle('');
              }}
              className="btn btn-secondary btn-full-width"
              style={{ marginBottom: '10px' }}
            >
              ← Edit Information
            </button>
          </div>

          {/* eBay Title */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>eBay Title</h3>
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              style={{ ...editableFieldStyle, ...overLimitStyle(editedTitle, EBAY_TITLE_MAX) }}
            />
            <div style={counterStyle(editedTitle, EBAY_TITLE_MAX)}>
              {editedTitle.length}/{EBAY_TITLE_MAX}
              {editedTitle.length > EBAY_TITLE_MAX ? ' - too long for eBay' : ''}
            </div>
            <button
              onClick={() => copyToClipboard(editedTitle, 'title')}
              className="btn btn-primary btn-full-width"
              style={{
                backgroundColor: copySuccess === 'title' ? '#10b981' : '#2c5f2d'
              }}
            >
              {copySuccess === 'title' ? '✓ Copied!' : '📋 Copy Title'}
            </button>
          </div>

          {/* eBay Subtitle */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>eBay Subtitle</h3>
            <input
              type="text"
              value={editedSubtitle}
              onChange={(e) => setEditedSubtitle(e.target.value)}
              style={{ ...editableFieldStyle, ...overLimitStyle(editedSubtitle, EBAY_SUBTITLE_MAX) }}
            />
            <div style={counterStyle(editedSubtitle, EBAY_SUBTITLE_MAX)}>
              {editedSubtitle.length}/{EBAY_SUBTITLE_MAX}
              {editedSubtitle.length > EBAY_SUBTITLE_MAX ? ' - too long for eBay' : ''}
            </div>
            <button
              onClick={() => copyToClipboard(editedSubtitle, 'subtitle')}
              className="btn btn-primary btn-full-width"
              style={{
                backgroundColor: copySuccess === 'subtitle' ? '#10b981' : '#2c5f2d'
              }}
            >
              {copySuccess === 'subtitle' ? '✓ Copied!' : '📋 Copy Subtitle'}
            </button>
          </div>

          {/* Payment Calculations */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>Calculated Monthly Payments</h3>
            {/* One line per tier the tenant sells, straight from the server's
                calculated rows. This block used to rebuild the server's old
                fixed five-bracket table ($99 / 20% / 25% / 35% / 50%) from the
                tiers on this page, with `|| 99`, `|| 18` and `|| 12` fallbacks
                that printed another business's offer whenever a bracket was not
                configured -- and it invented a "best rate >= that percentage"
                for the 25% and 35% rows, which are not tiers anybody sells. The
                server now returns exactly the rows it puts in the listing, so
                the admin preview and the published HTML cannot disagree. */}
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              {(generatedListing.calculatedPayments || []).map((row, index) => (
                <div key={index}>
                  {row.label} ({row.apr}% APR): ${row.monthly}/month
                  {row.isPopular ? ' ⭐' : ''}
                </div>
              ))}
            </div>
          </div>

          {/* HTML Preview */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>Listing Preview</h3>
            <div style={{
              border: '2px solid var(--color-border)',
              borderRadius: '4px',
              padding: '20px',
              backgroundColor: 'var(--color-surface)',
              maxHeight: '500px',
              overflowY: 'auto'
            }}>
              <div dangerouslySetInnerHTML={{ __html: generatedListing.html }} />
            </div>
          </div>

          {/* HTML Code */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>HTML Code (Ready for eBay)</h3>
            <textarea
              value={generatedListing.html}
              readOnly
              rows="10"
              style={{
                width: '100%',
                padding: '10px',
                /* Raised from 12px: this box is readOnly but still focusable,
                   and iOS zooms the viewport on focus below 16px. Costs a few
                   visible lines of the generated HTML. */
                fontSize: '16px',
                fontFamily: 'monospace',
                borderRadius: '4px',
                border: '1px solid var(--color-border)',
                marginBottom: '10px',
                backgroundColor: 'var(--color-surface-sunken)'
              }}
            />
            <button
              onClick={() => copyToClipboard(generatedListing.html, 'html')}
              className="btn btn-full-width"
              style={{
                padding: '12px',
                backgroundColor: copySuccess === 'html' ? '#10b981' : '#f4a460',
                color: 'white',
                fontSize: '18px',
                fontWeight: 'bold'
              }}
            >
              {copySuccess === 'html' ? '✓ HTML Copied to Clipboard!' : '📋 Copy HTML to Clipboard'}
            </button>
          </div>

          {/* Instructions */}
          <div className="card" style={{ 
            backgroundColor: 'var(--color-warning-bg)', 
            border: '2px solid #f4a460',
            marginBottom: '20px'
          }}>
            <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>📝 Next Steps</h3>
            <ol style={{ fontSize: '14px', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>Copy the eBay Title and paste into eBay's title field</li>
              <li>Copy the eBay Subtitle and paste into eBay's subtitle field (optional)</li>
              <li>Copy the HTML Code and paste into eBay's description (use HTML mode)</li>
              <li>Add your property photos to the eBay listing</li>
              <li>Set your starting bid low; the winning bid becomes the down payment. Buy It Now is optional and offers the property at your smallest configured down payment on financing terms.</li>
              <li>Choose 7-day auction duration</li>
              <li>Publish your listing!</li>
            </ol>
          </div>
        </>
      )}
    </div>
  );
}

export default EbayListingGenerator;